<template>
  <aside class="left-sidebar">
    <div class="scroll-sidebar">
      <nav class="sidebar-nav">
        <ul class="sidebarnav">
          <li class="nav-small-cap">
            <i class="mdi mdi-dots-horizontal"></i>
            <span class="hide-menu">Main Menu</span>
          </li>
          <li class="sidebar-item">
            <router-link @click.native="onPageChange" class="sidebar-link waves-effect waves-dark" to="/dashboard">
              <i class="nav-icon fas fa-home"></i>
              <span class="menu-text hide-menu">Dashboard</span>
            </router-link>
          </li>

          <template v-if="isDirector">
            <li class="nav-small-cap">
              <i class="mdi mdi-dots-horizontal"></i>
              <span class="hide-menu">Workspace</span>
            </li>
            <li
              v-for="ws in workspaces"
              :key="ws.id"
              class="sidebar-item"
              :class="{ active: isActiveWorkspace(ws.id) }"
            >
              <router-link
                @click.native="onPageChange"
                class="sidebar-link waves-effect waves-dark"
                :to="`/workspaces/${ws.id}`"
              >
                <i class="nav-icon fas fa-th-large"></i>
                <span class="menu-text hide-menu">{{ ws.name }}</span>
              </router-link>
            </li>
            <li class="sidebar-item">
              <a href="javascript:void(0)" class="sidebar-link waves-effect waves-dark" @click="onCreateWorkspace">
                <i class="nav-icon fas fa-plus"></i>
                <span class="menu-text hide-menu">New Workspace</span>
              </a>
            </li>

            <li class="nav-small-cap">
              <i class="mdi mdi-dots-horizontal"></i>
              <span class="hide-menu">Device</span>
            </li>
            <li class="sidebar-item" :class="{ active: isDevicesRoute }">
              <router-link @click.native="onPageChange" class="sidebar-link waves-effect waves-dark" to="/devices">
                <i class="nav-icon fas fa-server"></i>
                <span class="menu-text hide-menu">Devices</span>
              </router-link>
            </li>

            <li class="nav-small-cap">
              <i class="mdi mdi-dots-horizontal"></i>
              <span class="hide-menu">Automation</span>
            </li>
            <li class="sidebar-item" :class="{ active: isMacrosRoute }">
              <router-link @click.native="onPageChange" class="sidebar-link waves-effect waves-dark" to="/macros">
                <i class="nav-icon fas fa-play-circle"></i>
                <span class="menu-text hide-menu">Macros</span>
              </router-link>
            </li>
            <li class="sidebar-item" :class="{ active: isQlcMacrosRoute }">
              <router-link @click.native="onPageChange" class="sidebar-link waves-effect waves-dark" to="/qlc-macros">
                <i class="nav-icon fas fa-lightbulb"></i>
                <span class="menu-text hide-menu">QLC Macros</span>
              </router-link>
            </li>
          </template>

          <li class="nav-small-cap">
            <i class="mdi mdi-dots-horizontal"></i>
            <span class="hide-menu">Account</span>
          </li>
          <li class="sidebar-item">
            <router-link @click.native="onPageChange" class="sidebar-link waves-effect waves-dark" to="/profile">
              <i class="nav-icon fas fa-user"></i>
              <span class="menu-text hide-menu">Profile</span>
            </router-link>
          </li>
        </ul>
      </nav>
    </div>

    <modal name="create-workspace" height="auto" :width="420" :adaptive="true">
      <div class="p-4">
        <h5 class="mb-3">New Workspace</h5>
        <div class="form-group">
          <label class="small text-muted">Name</label>
          <input
            v-model="createForm.name"
            class="form-control"
            maxlength="100"
            placeholder="Workspace name"
            @keyup.enter="saveCreateWorkspace"
          >
        </div>
        <div class="d-flex justify-content-end">
          <button class="btn btn-light mr-2" @click="$modal.hide('create-workspace')">Cancel</button>
          <button
            class="btn btn-primary"
            :disabled="!createForm.name.trim() || creating"
            @click="saveCreateWorkspace"
          >
            {{ creating ? 'Creating...' : 'Create' }}
          </button>
        </div>
      </div>
    </modal>
  </aside>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  data() {
    return {
      createForm: { name: '' },
      creating: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['role']),
    ...mapGetters('workspace', ['workspaces']),
    isDirector() {
      return this.role === 'director' || this.role === 'admin'
    },
    isDevicesRoute() {
      return this.$route.path.startsWith('/devices')
    },
    isMacrosRoute() {
      return this.$route.path === '/macros' || this.$route.path.startsWith('/macros/')
    },
    isQlcMacrosRoute() {
      return this.$route.path.startsWith('/qlc-macros')
    },
  },
  watch: {
    isDirector: {
      immediate: true,
      handler(val) {
        if (val) this.fetchWorkspaces()
      },
    },
  },
  mounted() {
    this.initTemplate()
  },
  methods: {
    ...mapActions('workspace', ['fetchWorkspaces', 'createWorkspace']),
    initTemplate() {
      const element = document.getElementById('main-wrapper')
      if (!element) return
      this.$adminTemplate.init(element, {
        Theme: false,
        Layout: 'vertical',
        LogoBg: 'skin6',
        NavbarBg: 'skin6',
        SidebarType: 'full',
        SidebarColor: 'skin6',
        SidebarPosition: true,
        HeaderPosition: true,
        BoxedLayout: false,
      })
      this.$adminTemplate.preloader()
      this.$adminTemplate.expandLogo()
    },
    onPageChange() {
      this.$adminTemplate.handleSidebarMobile(true)
    },
    isActiveWorkspace(id) {
      return Number(this.$route.params.id) === id
    },
    onCreateWorkspace() {
      this.createForm = { name: '' }
      this.creating = false
      this.$modal.show('create-workspace')
    },
    async saveCreateWorkspace() {
      const name = this.createForm.name.trim()
      if (!name || this.creating) return
      this.creating = true
      try {
        const ws = await this.createWorkspace(name)
        this.$modal.hide('create-workspace')
        this.onPageChange()
        this.$router.push(`/workspaces/${ws.id}`)
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Failed to create workspace')
      } finally {
        this.creating = false
      }
    },
  },
}
</script>
